// Reusable campaign card component for influencers
// Used in both dashboard and explore campaigns page

import React, { useState } from 'react';
import { deliverableLabels } from '../lib/deliverables.js';
import { applyToCampaign, hasAppliedToCampaign } from '../lib/campaignApplication.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { APPLICATION_STATUSES, normalizeApplicationStatus } from '../lib/applicationStatus.js';
import type { Timestamp } from 'firebase/firestore';

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function dollars(cents?: number): string {
  if (typeof cents !== 'number') return '-';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatMonthDay(d?: Timestamp): string | null {
  if (!d || typeof (d as any)?.toDate !== 'function') return null;
  return d.toDate().toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

function dateRange(start?: Timestamp, end?: Timestamp, deadline?: Timestamp | string): string {
  const s = formatMonthDay(start);
  const e = formatMonthDay(end);
  if (s && e) return `${s}  ${e}`.replace('\u0014\u0014', '–');
  // Handle deadline as string (free-form text) or Timestamp (legacy)
  if (typeof deadline === 'string') {
    return deadline ? `Deadline: ${deadline}` : '';
  }
  const d = formatMonthDay(deadline);
  return d ? `Deadline: ${d}` : '';
}

function compLine(comp?: any, collaborationType?: string): string {
  if (collaborationType && typeof collaborationType === 'string') return collaborationType;
  if (!comp) return 'Collaboration';
  if (Array.isArray(comp)) {
    const normalized = comp
      .map((v) => (typeof v === 'string' ? v.toLowerCase() : v))
      .filter(Boolean);
    const parts: string[] = [];
    if (normalized.includes('paid')) parts.push('Paid');
    if (normalized.includes('gift') || normalized.includes('product')) parts.push('Gift');
    return parts.length > 0 ? parts.join(' + ') : 'Collaboration';
  }
  return String(comp);
}

function BrandHeader({ name, logoUrl }: { name?: string; logoUrl?: string | null }) {
  const getInitials = (brandName?: string) => {
    if (!brandName) return 'BR';
    const words = brandName.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
      {logoUrl ? (
        <img src={logoUrl} alt={name || 'Brand'} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
          {getInitials(name)}
        </div>
      )}
      <span className="text-sm font-medium text-gray-900">{name || 'Brand'}</span>
    </div>
  );
}

export type InfluencerCampaignCardProps = {
  campaign: {
    id: string;
    data: {
      title?: string;
      name?: string;
      campaignName?: string;
      bio?: string;
      description?: string;
      brief?: string;
      budgetUSD?: number;
      budget?: number;
      payment_cents?: number;
      budget_cents?: number;
      deadline?: Timestamp | string;
      startDate?: Timestamp;
      endDate?: Timestamp;
      compensation?: any;
      collaborationType?: string;
      brandName?: string;
      brandId?: string;
      brandUid?: string;
      productLink?: string;
      hasPhysicalGift?: boolean;
      shippingFromCountry?: string;
      shippingFrom?: string;
      shippingNotes?: string;
      revisionPolicy?: string;
      deliverables?: string[];
    };
  };
  brandLogoUrl?: string | null;
  hasApplied: boolean;
  applicationStatus?: string; // Optional: actual application status (e.g., "accepted", "pending_review")
  onApply?: () => Promise<void> | void;
};

export default function InfluencerCampaignCard({
  campaign,
  brandLogoUrl,
  hasApplied: initialHasApplied,
  applicationStatus,
  onApply,
}: InfluencerCampaignCardProps) {
  const { id, data } = campaign;
  const { user, profile } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasApplied, setHasApplied] = useState(initialHasApplied);
  const [isApplying, setIsApplying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Payment calculation
  const budgetUSD = toNumberOrNull(data?.budgetUSD);
  const centsFromFields = toNumberOrNull(data?.payment_cents ?? data?.budget_cents);
  const dollarsField = toNumberOrNull(data?.budget);
  const payCents =
    budgetUSD !== null
      ? Math.round(budgetUSD * 100)
      : centsFromFields !== null
      ? centsFromFields
      : dollarsField !== null
      ? Math.round(dollarsField * 100)
      : undefined;
  const dr = dateRange(data?.startDate, data?.endDate, data?.deadline);

  // Extract campaign details
  const campaignTitle =
    data?.title ?? data?.name ?? data?.campaignName ?? data?.collaborationType ?? 'Untitled Campaign';
  const bio = data?.bio ?? data?.description ?? data?.brief ?? '';
  const collaborationType = data?.collaborationType || compLine(data?.compensation, data?.collaborationType);
  const productLink = data?.productLink;
  const shippingFrom = data?.shippingFromCountry ?? data?.shippingFrom;
  const shippingNotes = data?.shippingNotes;
  const hasPhysicalGift = data?.hasPhysicalGift ?? false;
  const revisionPolicy = data?.revisionPolicy;
  const paymentAmount = dollars(payCents);

  const handleApply = async () => {
    if (!user || profile?.userType !== 'influencer') {
      alert('Please sign in as an influencer to apply.');
      return;
    }

    if (hasApplied || isApplying) return;

    setIsApplying(true);

    try {
      // Fetch influencer profile for metrics
      const profileRef = doc(db, 'users', user.uid);
      const profileSnap = await getDoc(profileRef);
      const influencerProfile = profileSnap.exists() ? profileSnap.data() : {};

      await applyToCampaign({
        campaignId: id,
        influencerId: user.uid,
        campaignData: data,
        influencerProfile,
        influencerEmail: user.email || null,
      });

      setHasApplied(true);
      setShowSuccess(true);
      
      // Call optional callback
      if (onApply) {
        await onApply();
      }
    } catch (error: any) {
      console.error('Error applying to campaign:', error);
      const errorMessage = error?.message || 'Something went wrong. Please try again.';
      alert(errorMessage);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <div className="rounded-2xl border shadow-sm overflow-hidden" style={{ backgroundColor: '#E8FFF2' }}>
        <BrandHeader name={data?.brandName} logoUrl={brandLogoUrl} />
        <div className="p-3">
          {/* Always visible: Title, Payment */}
          <div className="text-base font-semibold line-clamp-1">{campaignTitle}</div>
          <div className="mt-1 text-sm">
            <span className="font-medium">Payment:</span> {paymentAmount}
          </div>

          {/* Collapsible expanded content */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-[1000px] opacity-100 mt-3' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="space-y-2 text-sm text-gray-700 border-t border-gray-200 pt-3 mt-3">
              {/* Collaboration Type */}
              {collaborationType && (
                <div>
                  <span className="font-semibold">Type:</span> <span className="text-gray-600">{collaborationType}</span>
                </div>
              )}

              {/* Bio/Description */}
              {bio && (
                <div>
                  <span className="font-semibold">Bio:</span> <span className="text-gray-600">{bio}</span>
                </div>
              )}

              {/* Deliverables */}
              {data?.deliverables && Array.isArray(data.deliverables) && data.deliverables.length > 0 && (
                <div>
                  <span className="font-semibold">Deliverables:</span>{' '}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {deliverableLabels(data.deliverables).map((label: string, i: number) => (
                      <span
                        key={i}
                        className="text-xs rounded-full border border-gray-300 bg-white px-2 py-0.5 text-gray-700"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {dr && (
                <div>
                  <span className="font-semibold">Timeline:</span> <span className="text-gray-600">{dr}</span>
                </div>
              )}

              {/* Payment (repeated in expanded view) */}
              <div>
                <span className="font-semibold">Payment:</span> <span className="text-gray-600">{paymentAmount}</span>
              </div>

              {/* Product Link */}
              {productLink && (
                <div>
                  <span className="font-semibold">Product:</span>{' '}
                  <a
                    href={productLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View product
                  </a>
                </div>
              )}

              {/* Physical Gift Info */}
              {hasPhysicalGift && (
                <div className="space-y-1">
                  <div>
                    <span className="font-semibold">Physical Gift:</span> <span className="text-gray-600">Yes</span>
                  </div>
                  {shippingFrom && (
                    <div>
                      <span className="font-semibold">Shipping From:</span> <span className="text-gray-600">{shippingFrom}</span>
                    </div>
                  )}
                  {shippingNotes && (
                    <div>
                      <span className="font-semibold">Shipping Notes:</span> <span className="text-gray-600">{shippingNotes}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Revision Policy */}
              {revisionPolicy && (
                <div>
                  <span className="font-semibold">Revision Policy:</span> <span className="text-gray-600">{revisionPolicy}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with toggle and Apply button */}
        <div className="mt-3 px-3 pb-3">
          <div className="flex items-center justify-between mb-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm underline text-muted-foreground hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'See less campaign details' : 'See more campaign details'}
            >
              {isExpanded ? 'See less' : 'See more'} {isExpanded ? '▲' : '▼'}
            </button>
            <button
              onClick={handleApply}
              disabled={hasApplied || isApplying}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                hasApplied
                  ? applicationStatus && normalizeApplicationStatus(applicationStatus) === APPLICATION_STATUSES.ACCEPTED
                    ? 'bg-green-100 text-green-700 cursor-not-allowed border-green-300'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-300'
                  : isApplying
                  ? 'bg-blue-400 text-white cursor-wait border-blue-400'
                  : 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600'
              }`}
              aria-label={hasApplied ? 'Already applied' : 'Apply to this campaign'}
              aria-disabled={hasApplied || isApplying}
            >
              {isApplying ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Applying…
                </>
              ) : hasApplied ? (
                applicationStatus && normalizeApplicationStatus(applicationStatus) === APPLICATION_STATUSES.ACCEPTED
                  ? 'Accepted'
                  : 'Applied'
              ) : (
                'Apply'
              )}
            </button>
          </div>
          {hasApplied && applicationStatus && normalizeApplicationStatus(applicationStatus) === APPLICATION_STATUSES.ACCEPTED && (
            <p className="text-xs text-slate-500 mt-1 text-right">See email for next steps.</p>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowSuccess(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#E8FFF2' }}
          >
            <h2 className="text-2xl font-bold mb-2 text-gray-900">Application Submitted!</h2>
            <p className="text-sm text-gray-700 mb-6">
              Please allow the brand to review applications. You'll be notified if you're chosen to work on the collaboration.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSuccess(false)}
                className="flex-1 px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              >
                Stay Here
              </button>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  window.location.href = '/dashboard';
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

